import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { TailSpin } from 'react-loader-spinner';
import ReviewsLanding from './pages/ReviewsLanding';
import SurveyLanding from './pages/SurveyLanding';
import ThankYou from './pages/ThankYou';
import ErrorPage from './pages/ErrorPage';
import WrongUrl from './pages/WrongUrl';
import ApiMethods from './ApiHelper/ApiMethods';
import apiMethodsInstance from './ApiHelper/ApiMethods';
import './App.css';
import Contacted from './pages/Contacted';

function App() {
    return (
        <Router>
            <AppRoutes />
        </Router>
    );
}

function AppRoutes() {
    const [surveyData, setSurveyData] = useState(null);
    const [hasRedirected, setHasRedirected] = useState(false);
    const [isRedirecting, setIsRedirecting] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        if (hasRedirected || isRedirecting) return;

        const searchParams = new URLSearchParams(location.search);
        const appsGuid = searchParams.get('agt');
        const requestId = searchParams.get('req');
        const url = window.location.href;
        let contactedOriginalUrl = '';

        const redirectOldUrls = async () => {
            try {
                const pathname = location.pathname;

                // Review
                if (pathname.toLowerCase().includes('/reviews/agent_review.aspx')) {
                    const appsGuid = searchParams.get('agt');
                    if (appsGuid) {
                        navigate(`/review/${appsGuid}`);
                        setHasRedirected(true);
                        return;
                    }
                }

                // Survey
                if (pathname.includes('/reviews/agent_experience.aspx') && requestId) {
                    navigate(`/survey/${appsGuid}/${requestId}`);
                    setHasRedirected(true);
                    return;
                }

                // Contacted             
                const contactedMatch = pathname.match(/^\/contacted\/([a-zA-Z0-9-]+)\/([a-zA-Z0-9-]+)$/i);
                contactedOriginalUrl = url;

                if (contactedMatch) {
                    const firstParam = contactedMatch[1];
                    const secondParam = contactedMatch[2];

                    if (secondParam === '0') {
                        getContactedAndRedirect(null, firstParam);
                    } else {
                        getContactedAndRedirect(firstParam, secondParam);
                    }
                    setHasRedirected(true);
                    return;
                }

                // Contacted Old Url (without '/0')
                if (pathname.includes('/reviews/thank_you.aspx') || /contacted\/([a-zA-Z0-9-]+)/.test(pathname)) {
                    let requestId;

                    if (pathname.includes('/reviews/thank_you.aspx')) {
                        const searchParams = new URLSearchParams(location.search);
                        requestId = searchParams.get('req');
                    }

                    const match = pathname.match(/contacted\/([a-zA-Z0-9-]+)/);
                    if (!requestId && match) {
                        requestId = match[1];
                    }

                    if (requestId) {
                        getContactedAndRedirect(null, requestId);
                        setHasRedirected(true);
                        return;
                    }
                }
            } catch (error) {
                const errorDetails = {
                    message: "REDIRECT OLD URL - An error occurred while fetching a Custom Url.",
                    error: error.message,
                    stack: error.stack,
                    url: window.location.href,
                };

                ApiMethods.logError(errorDetails, navigator.userAgent)
                    .finally(() => {
                        setIsRedirecting(false);

                        const hostname = window.location.hostname.toLowerCase();
                        const searchParams = new URLSearchParams(window.location.search);
                        const isGTC = searchParams.get('gtc') === 'true' || hostname.includes('globaltravelcollection.com');

                        let logo = 'https://agents.travelleaders.com/Reviews/images/LogoTL_59.png';
                        let linkHref = 'https://www.travelleaders.com/contact_us';
                        let contactLink = 'https://www.travelleaders.com/contact_us';
                        let header = 'Oops! Something went wrong';
                        let message = 'If the error persists, ';

                        if (isGTC) {
                            logo = 'https://www.globaltravelcollection.com/images/logo-GTC-black.svg';
                            linkHref = 'https://www.globaltravelcollection.com/contact-us';
                            contactLink = 'https://www.globaltravelcollection.com/contact-us';
                            header = 'Oops! Something went wrong.';
                            message = '';
                        }

                        navigate('/error', {
                            state: {
                                logo,
                                linkHref,
                                linkText: 'please contact us.',
                                header,
                                message: message ? (
                                    <>
                                        {message} <a href={contactLink} target="_blank" rel="noopener noreferrer">please contact us.</a>
                                    </>
                                ) : null,
                            }
                        });
                    });

                return;
            }
        };

        const getContactedAndRedirect = async (appsGuid, requestId) => {
            if (isRedirecting) return;

            const navigateToError = (logoOverride) => {
                const hostname = window.location.hostname.toLowerCase();
                const searchParams = new URLSearchParams(window.location.search);
                const isGTC = searchParams.get('gtc') === 'true' || hostname.includes('globaltravelcollection.com');

                // Default (Travel Leaders)
                let logo = 'https://agents.travelleaders.com/Reviews/images/LogoTL_59.png';
                let linkHref = 'https://www.travelleaders.com/contact_us';
                let contactLink = 'https://www.travelleaders.com/contact_us';
                let header = 'Oops! Something went wrong';
                let message = '';

                // Dream Vacations
                if (hostname.includes('agents.dreamvacationsagentdirectory.com')) {
                    logo = 'https://www.dreamvacations.com/images/home-based/webresources/images/logos/dream-vacations-logo-color.svg';
                    linkHref = 'https://www.dreamvacations.com/vacation/contact/get-in-touch';
                    contactLink = linkHref;
                    header = 'Oops! Something went wrong.';
                    message = '';
                }

                // Cruise One
                if (hostname.includes('agents.cruiseoneagentdirectory.com')) {
                    logo = 'https://www.cruiseone.com/images/home-based/webresources/images/logos/cruise-one-logo-color.svg';
                    linkHref = 'https://www.cruiseone.com/vacation/contact/get-in-touch';
                    contactLink = linkHref;
                    header = 'Oops! Something went wrong.';
                    message = '';
                }

                // GTC (Global Travel Collection)
                if (isGTC) {
                    logo = 'https://www.globaltravelcollection.com/images/logo-GTC-black.svg';
                    linkHref = 'https://www.globaltravelcollection.com/contact-us';
                    contactLink = linkHref;
                    header = 'Oops! Something went wrong.';
                    message = '';
                }

                navigate('/wrongUrl', {
                    state: {
                        logo: logoOverride || logo,
                        linkHref,
                        linkText: '',
                        header,
                        message,
                        contactLink,
                    }
                });
            };


            try {
                setIsRedirecting(true);
                await apiMethodsInstance.login();

                const contactedResponse = await apiMethodsInstance.getContacted(appsGuid, requestId);

                let contactedParsedResponse;
                try {
                    contactedParsedResponse = JSON.parse(contactedResponse.data);
                } catch (parseError) {
                    const errorDetails = {
                        message: "GET CONTACTED AND REDIRECT - Failed to parse JSON from getContacted API response",
                        error: parseError.toString(),
                        responseData: contactedResponse.data,
                        appsGuid,
                        requestId,
                        url: window.location.href
                    };

                    await ApiMethods.logError(errorDetails, navigator.userAgent);
                    navigateToError();
                    return;
                }

                const responseEligible = contactedParsedResponse.ResponseEligible;
                let contactedParsedSuccess = false;

                try {
                    contactedParsedSuccess = JSON.parse(contactedResponse.success);
                } catch (parseSuccessError) {
                    const errorDetails = {
                        message: "GET CONTACTED AND REDIRECT - Failed to parse success flag from getContacted API response",
                        error: parseSuccessError.toString(),
                        successData: contactedResponse.success,
                        appsGuid,
                        requestId,
                        url: window.location.href
                    };

                    await ApiMethods.logError(errorDetails, navigator.userAgent);
                    navigateToError(contactedParsedResponse.Agency?.ReviewsLogo);
                    return;
                }

                if (contactedParsedSuccess) {
                    if (responseEligible) {
                        navigate(`/contacted/${appsGuid}/${requestId}`, {
                            state: {
                                appsGuid,
                                requestId,
                                logo: contactedParsedResponse.Agency?.ReviewsLogo,
                                agency: contactedParsedResponse.Agency?.HomePageLink,
                                firstName: contactedParsedResponse.FirstName,
                                lastName: contactedParsedResponse.LastName,
                                profilePhoto: contactedParsedResponse.ProfilePhoto,
                                requestUrl: contactedOriginalUrl,
                            }
                        });
                    } else {
                        navigate(`/thankyou`, {
                            state: {
                                logo: contactedParsedResponse.Agency?.ReviewsLogo,
                                agency: contactedParsedResponse.Agency?.HomePageLink,
                            }
                        });
                    }
                } else {
                    const errorDetails = {
                        message: "GET CONTACTED AND REDIRECT - API responded with success: false",
                        response: contactedParsedResponse,
                        appsGuid,
                        requestId,
                        url: window.location.href
                    };

                    await ApiMethods.logError(errorDetails, navigator.userAgent);
                    navigateToError(contactedParsedResponse.Agency?.ReviewsLogo);
                }
            } catch (error) {
                const errorDetails = {
                    message: "APPJS | GET CONTACTED AND REDIRECT - Unexpected error in getContactedAndRedirect",
                    error: error.toString(),
                    appsGuid,
                    requestId,
                    url: window.location.href
                };

                await ApiMethods.logError(errorDetails, navigator.userAgent);
                navigateToError();
            } finally {
                setIsRedirecting(false);
            }
        };


        const fetchCustomUrl = async () => {
            if (appsGuid) return;

            const urlParts = window.location.hostname.split('.');
            const domain = urlParts[2];
            const agentPath = location.pathname.split('/')[1];

            if (domain && agentPath && !['survey', 'review', 'contacted', 'error', 'wrongurl'].includes(agentPath.toLowerCase())) {
                try {
                    setIsRedirecting(true);

                    const apiResponse = await apiMethodsInstance.getCustomUrl(`${domain}/${agentPath}`);

                    if (!apiResponse.success) {
                        throw new Error('Custom URL fetch failed: success flag is false');
                    }

                    const parsedData = JSON.parse(apiResponse.data);
                    if (parsedData.URL) {
                        window.location.href = parsedData.URL;
                    } else {
                        throw new Error('Custom URL fetch failed: URL not present in parsed data');
                    }

                } catch (error) {
                    const errorDetails = {
                        message: "APPJS | FETCH CUSTOM URL - An error occurred while fetching a Custom Url.",
                        error: error.message,
                        stack: error.stack,
                        url: window.location.href,
                    };

                    ApiMethods.logError(errorDetails, navigator.userAgent)
                        .finally(() => {
                            setIsRedirecting(false);

                            const hostname = window.location.hostname.toLowerCase();
                            const searchParams = new URLSearchParams(window.location.search);
                            const isGTC = searchParams.get('gtc') === 'true' || hostname.includes('globaltravelcollection.com');

                            let logo = 'https://agents.travelleaders.com/Reviews/images/LogoTL_59.png';
                            let linkHref = 'https://www.travelleaders.com/contact_us';
                            let contactLink = 'https://www.travelleaders.com/contact_us';
                            let header = 'Oops! Something went wrong';
                            let message = 'If the error persists, ';

                            if (isGTC) {
                                // Override Global Travel Collection
                                logo = 'https://www.globaltravelcollection.com/images/logo-GTC-black.svg';
                                linkHref = 'https://www.globaltravelcollection.com/contact-us';
                                contactLink = 'https://www.globaltravelcollection.com/contact-us';
                                header = 'Oops! Something went wrong.';
                                message = '';
                            }

                            navigate('/error', {
                                state: {
                                    logo,
                                    linkHref,
                                    linkText: 'please contact us.',
                                    header,
                                    message: message ? (
                                        <>
                                            {message} <a href={contactLink} target="_blank" rel="noopener noreferrer">please contact us.</a>
                                        </>
                                    ) : null,
                                }
                            });
                        });
                }
            }
        };


        if (!hasRedirected) {
            const parts = location.pathname.split('/');
            const baseRoute = parts[1];

            if (baseRoute && !appsGuid) {
                fetchCustomUrl();
                setHasRedirected(true);
            } else {
                const navigateToError = (customMessage) => {
                    const hostname = window.location.hostname.toLowerCase();
                    const searchParams = new URLSearchParams(window.location.search);
                    const isGTC = searchParams.get('gtc') === 'true' || hostname.includes('globaltravelcollection.com');

                    let logo = 'https://agents.travelleaders.com/Reviews/images/LogoTL_59.png';
                    let linkHref = 'https://www.travelleaders.com/contact_us';
                    let contactLink = 'https://www.travelleaders.com/contact_us';
                    let header = 'Oops! Something went wrong';
                    let message = 'If the error persists, ';

                    if (isGTC) {
                        logo = 'https://www.globaltravelcollection.com/images/logo-GTC-black.svg';
                        linkHref = 'https://www.globaltravelcollection.com/contact-us';
                        contactLink = 'https://www.globaltravelcollection.com/contact-us';
                        header = 'Oops! Something went wrong.';
                        message = '';
                    }

                    navigate('/error', {
                        state: {
                            logo,
                            linkHref,
                            linkText: 'please contact us.',
                            header,
                            message: message ? (
                                <>
                                    {message}
                                    <a href={contactLink} target="_blank" rel="noopener noreferrer">please contact us.</a>
                                </>
                            ) : null,
                        }
                    });
                };

                if (baseRoute === 'Survey') {
                    const appsGuid = parts[2];
                    const requestId = parts[3];
                    if (appsGuid && requestId) {
                        navigate(`/survey/${appsGuid}/${requestId}`);
                    } else {
                        const errorDetails = {
                            message: "APPJS | HAS REDIRECTED - An error occurred while redirecting a Survey URL.",
                            url: window.location.href,
                        };

                        ApiMethods.logError(errorDetails, navigator.userAgent)
                            .finally(() => {
                                setIsRedirecting(false);
                                navigateToError();
                            });
                    }
                }

                if (baseRoute === 'Review') {
                    const appsGuid = parts[2];
                    if (appsGuid) {
                        navigate(`/review/${appsGuid}`);
                    } else {
                        const errorDetails = {
                            message: "APPJS | HAS REDIRECTED - An error occurred while redirecting a Review URL.",
                            url: window.location.href,
                        };

                        ApiMethods.logError(errorDetails, navigator.userAgent)
                            .finally(() => {
                                setIsRedirecting(false);
                                navigateToError();
                            });
                    }
                }

                if (baseRoute === 'Contacted') {
                    const appsGuid = parts[2];
                    const requestId = parts[3];
                    if (appsGuid && requestId) {
                        getContactedAndRedirect(appsGuid, requestId);
                    } else {
                        const errorDetails = {
                            message: "APPJS | HAS REDIRECTED - An error occurred while redirecting a Contacted URL.",
                            url: window.location.href,
                        };

                        ApiMethods.logError(errorDetails, navigator.userAgent)
                            .finally(() => {
                                setIsRedirecting(false);
                                navigateToError();
                            });
                    }
                }
            }
        }

        redirectOldUrls();

    }, [location, navigate, hasRedirected, isRedirecting]);

    if (isRedirecting) {
        return (
            <div className="flex justify-center items-center h-screen">
                <TailSpin
                    height="100"
                    width="100"
                    color="#0B829E"
                    ariaLabel="tail-spin-loading"
                    radius="1"
                    visible={true}
                />
            </div>
        );
    }

    return (
        <Routes>
            <Route path="/" element={<WrongUrl />} />
            <Route path="/review/:appsGuid" element={<ReviewsLanding />} />
            <Route path="/survey/:appsGuid/:requestId" element={<SurveyLanding />} />
            <Route path="/contacted/:appsGuid/:requestId" element={<Contacted />} />
            <Route path="/thankyou" element={<ThankYou />} />
            <Route path="/error" element={<ErrorPage />} />
            <Route path="/wrongUrl" element={<WrongUrl />} />
            <Route path="*" element={<WrongUrl />} />
        </Routes>
    );
}

export default App;